import React, { useState, useEffect } from 'react';
import { X, ArrowUp } from 'lucide-react';
import { validateEmail, checkRateLimit, validateHoneypot, sanitizeEmail } from '../utils/validation';
import { trackFormSubmission, trackError, trackUserInteraction } from '../utils/analytics';

const StickyBar = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [formData, setFormData] = useState({ email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [honeypot, setHoneypot] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const shouldShow = scrolled > 800 && !isDismissed;
      setIsVisible(shouldShow);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Honeypot validation
      if (!validateHoneypot(honeypot)) {
        trackError('Bot detected', 'sticky_bar_honeypot');
        throw new Error('Invalid submission');
      }

      // Rate limiting
      if (!checkRateLimit()) {
        setError('Too many submissions. Please wait a moment.');
        trackError('Rate limit exceeded', 'sticky_bar_submission');
        return;
      }

      // Sanitize and validate email
      const sanitizedEmail = sanitizeEmail(formData.email);
      const emailValidation = validateEmail(sanitizedEmail);
      
      if (!emailValidation.isValid) {
        setError(emailValidation.error!);
        trackError('Email validation failed', 'sticky_bar_validation', { email: sanitizedEmail });
        return;
      }

      // Track submission
      trackFormSubmission('sticky-bar', sanitizedEmail);

      // In development mode, simulate success
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const waitlistData = {
          email: sanitizedEmail,
          name: '',
          role: '',
          timestamp: new Date().toISOString(),
          id: Date.now(),
          source: 'sticky-bar'
        };
        
        const existingData = JSON.parse(localStorage.getItem('contax-waitlist') || '[]');
        existingData.push(waitlistData);
        localStorage.setItem('contax-waitlist', JSON.stringify(existingData));
        
        setIsDismissed(true);
        return;
      }

      // Production: Submit to Netlify Forms using the correct form name
      const response = await fetch('/', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          'form-name': 'contax-sticky-waitlist', // Updated to match the hidden form
          'email': sanitizedEmail,
          'name': '',
          'role': '',
          'timestamp': new Date().toISOString(),
          'source': 'sticky-bar',
          'bot-field': honeypot
        }).toString()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Store in localStorage
      const waitlistData = {
        email: sanitizedEmail,
        name: '',
        role: '',
        timestamp: new Date().toISOString(),
        id: Date.now(),
        source: 'sticky-bar'
      };
      
      const existingData = JSON.parse(localStorage.getItem('contax-waitlist') || '[]');
      existingData.push(waitlistData);
      localStorage.setItem('contax-waitlist', JSON.stringify(existingData));
      
      setIsDismissed(true);
    } catch (error) {
      console.error('Form submission error:', error);
      setError('Something went wrong. Please try again.');
      trackError(error instanceof Error ? error.message : 'Unknown error', 'sticky_bar_submission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    trackUserInteraction('sticky_bar_dismiss');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-2xl z-50 transform transition-transform duration-300">
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div>
                <h3 className="font-semibold text-lg">Don't miss out on early access!</h3>
                <p className="text-blue-100 text-sm">Join 12,847+ professionals already waiting for ContaX</p>
              </div>
              
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-2 min-w-0 flex-shrink-0">
                {/* Honeypot field */}
                <input
                  type="text"
                  name="bot-field"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />
                
                <div className="flex flex-col">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ email: e.target.value })}
                    required
                    className="px-4 py-2 rounded-lg text-gray-900 placeholder-gray-500 min-w-0 flex-1"
                  />
                  {error && (
                    <p className="text-red-200 text-xs mt-1" role="alert">
                      {error}
                    </p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-white text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap flex items-center disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <>
                      Join Waitlist
                      <ArrowUp className="w-4 h-4 ml-2 rotate-45" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
          
          <button
            onClick={handleDismiss}
            className="ml-4 p-2 hover:bg-white/10 rounded-full transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickyBar;